'use client';

import type { ReactNode } from 'react';

import { cn } from '@ps/ui/utils';
import { AnimatePresence, motion } from 'motion/react';

import type { AnyVideo } from '../functions';
import { isPopClipVideo } from '../functions';
import { VideoActions } from './VideoActions';
import { type ButtonStyles, VideoFooter } from './VideoFooter';
import { VideoSeekbar } from './VideoSeekbar';

type VideoOverlayProps = {
  video: AnyVideo;
  visible: boolean;
  editable?: boolean;
  currentUserId?: string;
  buttonStyles?: ButtonStyles;
  isRealEstateListing?: boolean;
  listingId?: string;
  onBack?: () => void;
  onOpenComments?: () => void;
  onUpdated?: (video: AnyVideo) => void;
  onDeleted?: (video: AnyVideo) => void;
  ownerLink?: ReactNode;
  className?: string;
};

async function downloadTranscript(video: AnyVideo) {
  const attachment = (video as any).metaData!.transcriptAttachment as any;
  const url: string = attachment.url;
  const filename: string = attachment.filename ?? 'transcript';

  const response = await fetch(url);
  if (!response.ok) return;

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

export function VideoOverlay({
  video,
  visible,
  currentUserId,
  buttonStyles,
  isRealEstateListing,
  listingId,
  onBack,
  onOpenComments,
  onDeleted,
  ownerLink,
  className,
}: VideoOverlayProps) {
  const handleBack = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBack?.();
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            // This chrome sits ON the video — captions, username, action
            // buttons, the booking header — so it is a dark surface in every
            // theme, exactly like every video player on the web. Pinned here
            // rather than on a shell because two different shells render it
            // (`VideoModal` and the owner `series-video-player`), and one of
            // them also has a comments panel that SHOULD follow the theme.
            data-theme="dark"
            className={cn(
              'pointer-events-none absolute inset-0 z-10 flex flex-col',
              className,
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            {/* Back button - top left */}
            {onBack && (
              <div
                className="pointer-events-auto absolute z-20"
                style={{ left: 0, top: 20 }}
              >
                <button
                  type="button"
                  onClick={handleBack}
                  className="text-content-1 flex items-center justify-center"
                  style={{ width: 50, height: 50 }}
                >
                  <svg viewBox="0 0 50 50" fill="none" width={50} height={50}>
                    <path
                      d="M31.8236 23.8236L21.3527 23.8236L25.1681 20.0083C25.6273 19.5491 25.6273 18.8037 25.1681 18.3444C24.7088 17.8852 23.9634 17.8852 23.5042 18.3444L17.6805 24.1681C17.2213 24.6273 17.2213 25.3727 17.6805 25.832L23.5041 31.6556C23.9634 32.1148 24.7088 32.1148 25.168 31.6556C25.6269 31.1968 25.6273 30.4509 25.168 29.9917L21.3527 26.1764L31.8236 26.1764C32.4734 26.1764 33 25.6497 33 25C33.0004 24.3506 32.473 23.8232 31.8236 23.8236Z"
                      fill="#FFFFFF"
                    />
                  </svg>
                </button>
              </div>
            )}

            {/* Actions - right side */}
            <div
              className="pointer-events-auto absolute z-20"
              style={{ top: 15, right: 15 }}
            >
              <VideoActions
                video={video}
                currentUserId={currentUserId}
                isRealEstateListing={isRealEstateListing}
                onOpenComments={onOpenComments}
                onDelete={onDeleted}
              />
            </div>

            {/* Footer - bottom left */}
            <div
              className="pointer-events-auto relative z-20 mt-auto pb-10 max-md:pb-12"
              style={{ paddingLeft: 10, paddingRight: 10 }}
            >
              <VideoFooter
                video={video}
                buttonStyles={buttonStyles}
                isRealEstateListing={isRealEstateListing}
                listingId={listingId}
                ownerLink={ownerLink}
              />
            </div>

            {/* Seekbar - very bottom */}
            <div className="pointer-events-auto absolute inset-x-0 bottom-5 z-20 max-md:bottom-7">
              <VideoSeekbar />
            </div>

            {/* Transcript download */}
            {isPopClipVideo(video) && video.metaData?.transcriptAttachment && (
              <div
                className="pointer-events-auto absolute z-20"
                style={{ bottom: 20, right: 15 }}
              >
                <button
                  type="button"
                  className="text-content-1 flex items-center justify-center"
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(0, 0, 0, 0.15)',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    void downloadTranscript(video);
                  }}
                >
                  {/* Lucide Download icon — matches PopshopRN */}
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clickable area when overlay is hidden */}
      {!visible && <div className="absolute inset-0" />}
    </>
  );
}
